/**
 * RoadmapPreviewStrip — Gap 5: Curto/Médio prazo (esta semana + próximas 4).
 * Chip horizontal com semana atual + próximas 4 semanas, título de cada
 * aula, mudança de fase destacada.
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const PHASE_COLORS = ["#a855f7", "#84cc16", "#f59e0b", "#06b6d4", "#ef4444"];

export default function RoadmapPreviewStrip({ program }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/programs/${program}/roadmap-preview`, { headers: headers() });
        setData(await res.json());
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [program]);

  if (loading || !data?.upcoming?.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border p-4 mb-4"
      style={{
        borderColor: "rgba(139,92,246,0.35)",
        background: "linear-gradient(140deg, rgba(139,92,246,0.10) 0%, rgba(9,9,11,0.95) 60%)",
        boxShadow: "0 4px 18px rgba(139,92,246,0.15), inset 0 1px 0 rgba(139,92,246,0.2)",
      }}
      data-testid="roadmap-preview-strip"
    >
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-3.5 h-3.5 text-violet-400" />
        <p className="text-[10px] uppercase tracking-widest font-bold text-violet-400">Mapa · esta semana + próximas 4</p>
        <span className="text-[10px] text-zinc-500 ml-auto">
          {data.current_week}/{data.total_weeks}
        </span>
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {data.upcoming.map((w) => {
          const color = PHASE_COLORS[(w.phase_id - 1) % PHASE_COLORS.length];
          return (
            <div
              key={w.week}
              className="flex-shrink-0 w-40 p-3 rounded-xl border"
              style={{
                borderColor: w.is_current ? color : "#27272a",
                background: w.is_current ? `${color}18` : "rgba(24,24,27,0.7)",
                boxShadow: w.is_current ? `0 0 16px ${color}40` : "none",
              }}
              data-testid={`roadmap-preview-week-${w.week}`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <span
                  className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                  style={{ background: `${color}25`, color }}
                >
                  Sem {w.week} · F{w.phase_id}
                </span>
                {w.is_current && <span className="text-[9px] font-bold text-zinc-100">agora</span>}
              </div>
              {w.category_icon && (
                <div className="text-lg mb-0.5">{w.category_icon}</div>
              )}
              <p className="text-[11px] font-bold text-zinc-100 leading-tight line-clamp-2">
                {w.title_pt || w.teaser_pt}
              </p>
            </div>
          );
        })}
      </div>

      {data.phase_change && (
        <div className="mt-3 p-2 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center gap-2">
          <ChevronRight className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
          <p className="text-[11px] text-violet-300 leading-snug">
            Sem <span className="font-bold">{data.phase_change.at_week}</span> · muda para <span className="font-bold">Fase {data.phase_change.to_phase}</span> — o plano muda automaticamente
          </p>
        </div>
      )}
    </motion.div>
  );
}
